import React from 'react';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import styled from 'styled-components';
import { Money } from '../../shared/dto/EntityRepository';
import { useEditMode } from '../../shared/hooks/useEditMode';
import { StyledDivComponent } from '../../shared/dto/StyleCompRepository';
import { InputWork } from '../../shared/component/headless/input/InputBody';
import HeadlessInput from '../../shared/component/headless/input/HeadlessInput';
import { useValidation } from '../../shared/hooks/useValidation';
import toggleIcon from '../../asset/icon/toggle.png';
import { renderingPrice, cleanUpPrice, getCurrencySymbol } from '../../shared/util/renderingPrice';
import SelectBox from '../../shared/component/SelectBox';
import { CurrencyCode, DeviceType } from '../../shared/enum/EnumRepository';
import searchIcon from '../../asset/icon/see.png';
import searchNotIcon from '../../asset/icon/see_not.png';
import { useDeviceStore } from '../../shared/store/deviceStore';
import HintSmallWrapper from '../../shared/component/headless/button/HintSmallWrapper';
import { getHintMessage } from '../../shared/constants/hintMessages';
import { INPUT_PRICE_MIN_LENGTH, INPUT_PRICE_MAX_LENGTH } from '../../shared/constants/limits';

interface PriceInputProps {
  value: Money;
  onChange: (next: Money, field: 'amount' | 'currency') => void;
  isAmountMixed?: boolean;
  isCurrencyMixed?: boolean;
  StyledDiv: StyledDivComponent;
  isPOA?: boolean;
  onPOAToggle?: () => void;
  visible?: boolean;
  changeVisible?: () => void;
  changeVisibleMobile?: () => void;
}

const PriceInput: React.FC<PriceInputProps> = ({
  value,
  onChange,
  isAmountMixed = false,
  isCurrencyMixed = false,
  StyledDiv,
  isPOA = false,
  onPOAToggle,
  visible = true,
  changeVisible,
  changeVisibleMobile,
}) => {
  const { isEditMode } = useEditMode();
  const { checkType } = useValidation();
  const { deviceType } = useDeviceStore();
  const currency = value?.currency ?? CurrencyCode.USD;

  const handleChange = (newValue: string) => {
    if (!checkType('price', newValue)) {
      return;
    }

    // // 숫자가 아닌 문자 제거
    const cleanValue = cleanUpPrice(newValue);
    const amount = cleanValue ? parseInt(cleanValue, 10) : 0;
    const updatedPrice: Money = {
      amount,
      currency,
    };
    onChange(updatedPrice, 'amount');
  }

  const convertPriceToString = (price: Money): string => {
    if (isPOA) return "POA";
    if (isAmountMixed || isCurrencyMixed) return 'Mixed';
    const priceString = renderingPrice(price);
    if (priceString === "") return "";
    return `${getCurrencySymbol(currency)} ${priceString}`;
  }

  return (
    <>
      {isEditMode ? (
        <PriceInputContainer>
          <PriceInputComp $isVisible={visible}>
            {isPOA ?
              <POAComp>POA</POAComp> :
              <>
                <CurrencySelectWrapper $isMixed={isCurrencyMixed}>
                  <SelectBox<CurrencyCode | 'Mixed'>
                    value={isCurrencyMixed ? 'Mixed' : currency}
                    options={Object.values(CurrencyCode)}
                    handleChange={(next) => {
                      if (next !== 'Mixed') onChange({ amount: value?.amount ?? 0, currency: next }, 'currency');
                    }}
                    direction={false}
                  />
                </CurrencySelectWrapper>
                <HeadlessInput
                  value={isAmountMixed ? '' : renderingPrice(value)}
                  handleChange={(e) => handleChange(e.target.value)}
                  StyledInput={NumInput}
                  placeholder={'Price'}
                  minLimit={INPUT_PRICE_MIN_LENGTH}
                  maxLimit={INPUT_PRICE_MAX_LENGTH}
                />
              </>
            }
            <UniversalIconBtn
              size="small"
              border={false}
              isTransparent
              backgroundImg={toggleIcon}
              onClick={onPOAToggle}
            />
          </PriceInputComp>
          <HintSmallWrapper content={visible ? getHintMessage('SET_TO_PRIVATE') : getHintMessage('SET_TO_PUBLIC')} toLeft={true}>
            <UniversalIconBtn
              size="fit"
              border={false}
              isTransparent
              backgroundImg={visible ? searchIcon : searchNotIcon}
              onClick={deviceType === DeviceType.MOBILE ? changeVisibleMobile : changeVisible}
            />
          </HintSmallWrapper>
        </PriceInputContainer>
      ) : (
        visible && (isPOA || !!value?.amount) ? (
          <StyledDiv>{convertPriceToString(value)}</StyledDiv>
        ) : null
      )}
    </>
  );
};

const PriceInputContainer = styled.div`
  width: 100%;
  min-width: 80px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  ${({ theme }) => theme.typography.Body_04};
`;

const PriceInputComp = styled.div<{ $isVisible: boolean }>`
  display: flex;
  align-items: center;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0.5)};
`;

const NumInput = styled(InputWork)`
  min-width: 40px;
  text-align: left;
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};
`;

const CurrencySelectWrapper = styled.div<{ $isMixed: boolean }>`
  width: ${({ $isMixed }) => $isMixed ? '64px' : '40px'};
  flex-shrink: 0;
  margin-right: 8px;
  color: ${({ theme }) => theme.colors.color_Gray_04};
`;

const POAComp = styled.div`
  min-width: 40px;
  width: 7ch;
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_03};
  display: flex;
  justify-content: center;
  align-items: center;
`;

export default PriceInput;
